import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";

/**
 * Something went wrong that is **not** a permission decision.
 *
 * The distinction matters enough that `Refusal` exists separately: a manifest
 * refusal is a fact about a declaration somebody wrote down, and this is a
 * fact about a network. Rendering them the same way is how "the console is
 * broken" gets reported for a manifest that is working exactly as written.
 *
 * What it puts on screen, and why each part:
 *
 * - **The status and the kernel's `error` code**, because that is the string
 *   somebody will search the kernel's source for, and a paraphrase is not.
 * - **`parameter`**, when the kernel named one. A 400 that says *which*
 *   argument was wrong turns a bug report into a fix, and the kernel goes to
 *   the trouble of naming it — throwing that away here would waste the one
 *   thing it did that a generic error handler does not.
 * - **Retry, only when the caller offers one.** A transport failure is worth
 *   trying twice; a 403 never is, and a retry button on a refusal invites
 *   somebody to click it four times before reading the sentence.
 */
export default function ErrorState({
  failure,
  title,
  onRetry,
  retryLabel = "Try again",
  severity = "error",
  children,
  sx,
}) {
  const status = failure?.status;
  const code = failure?.error;
  const heading =
    title ||
    (status
      ? `${status}${code ? ` ${code}` : ""}`
      : // No status at all means the request never got an answer — DNS, CORS,
        // an aborted fetch. Saying "could not reach" rather than inventing a
        // code is the honest version, and it is also the one that points at
        // the right half of the stack.
        "Could not reach the kernel");

  return (
    <Alert severity={severity} variant="outlined" sx={sx}>
      <AlertTitle>{heading}</AlertTitle>
      {failure?.message ? <Box component="span">{failure.message}</Box> : null}
      {children}
      {failure?.parameter ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Parameter: <code>{failure.parameter}</code>
        </Typography>
      ) : null}
      {onRetry ? (
        <Box sx={{ mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={onRetry}>
            {retryLabel}
          </Button>
        </Box>
      ) : null}
    </Alert>
  );
}
